const MAX_INPUT_BYTES = 25 * 1024 * 1024;
const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.82;

export class TooLargeError extends Error {
  constructor() {
    super("Le fichier dépasse la taille maximale (25 Mo).");
    this.name = "TooLargeError";
  }
}

export class UnsupportedFormatError extends Error {
  constructor() {
    super("Format d'image non supporté.");
    this.name = "UnsupportedFormatError";
  }
}

export type ProcessedImage = {
  thumbUrl: string;
  base64: string;
};

const isHeic = (file: File): boolean => {
  const t = (file.type || "").toLowerCase();
  if (t === "image/heic" || t === "image/heif") return true;
  const name = file.name.toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
};

type Heic2AnyFn = (opts: {
  blob: Blob;
  toType?: string;
  quality?: number;
}) => Promise<Blob | Blob[]>;

async function convertHeicToJpegBlob(file: File): Promise<Blob> {
  const mod = (await import(
    /* webpackChunkName: "heic2any" */ "heic2any"
  )) as unknown as Heic2AnyFn | { default: Heic2AnyFn };
  const heic2any: Heic2AnyFn =
    typeof mod === "function" ? mod : (mod as { default: Heic2AnyFn }).default;
  const out = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: JPEG_QUALITY,
  });
  return Array.isArray(out) ? out[0] : out;
}

async function blobToImageBitmap(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob);
    } catch (e) {
      // fall through
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new UnsupportedFormatError());
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function getDims(source: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  if ("naturalWidth" in source) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
}

async function canvasToJpegBlob(
  source: ImageBitmap | HTMLImageElement,
  targetW: number,
  targetH: number
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new UnsupportedFormatError();
  ctx.drawImage(source as CanvasImageSource, 0, 0, targetW, targetH);
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new UnsupportedFormatError())),
      "image/jpeg",
      JPEG_QUALITY
    )
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.readAsDataURL(blob);
  });
}

export async function processForUpload(file: File): Promise<ProcessedImage> {
  if (file.size > MAX_INPUT_BYTES) {
    throw new TooLargeError();
  }

  let workingBlob: Blob = file;
  if (isHeic(file)) {
    workingBlob = await convertHeicToJpegBlob(file);
  }

  const source = await blobToImageBitmap(workingBlob);
  const { width, height } = getDims(source);
  const longest = Math.max(width, height);
  const scale = longest > MAX_EDGE_PX ? MAX_EDGE_PX / longest : 1;
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const jpegBlob = await canvasToJpegBlob(source, targetW, targetH);
  const [thumbUrl, base64] = await Promise.all([
    Promise.resolve(URL.createObjectURL(jpegBlob)),
    blobToBase64(jpegBlob),
  ]);

  return { thumbUrl, base64 };
}
